/**
 * Normaliza o payload de POST /login (MS-031).
 *
 * JWT em `access_token`. ACL/`id` preferem `user.*`, com fallback de `acl`/`_id` no raiz.
 */

export type LoginPayload = Record<string, unknown> & {
  access_token?: string;
  acl?: Record<string, unknown>;
  _id?: string;
  email?: string;
  user?: {
    id?: string;
    email?: string;
    acl?: Record<string, unknown>;
    [key: string]: unknown;
  };
  scopes?: string[];
};

export type NormalizedLogin = {
  accessToken: string;
  acl: Record<string, unknown> | null;
  userId: string | null;
  email: string | null;
  scopes: string[] | null;
  refreshToken: string | null;
  refreshExpiresIn: number | null;
  /** Payload original (meta / localStorage). */
  raw: LoginPayload;
};

const asRecord = (value: unknown): Record<string, unknown> | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }
  return value as Record<string, unknown>;
};

const asScopes = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  return value.filter((s): s is string => typeof s === 'string');
};

/** Aceita `refresh_token` string ou `{ id, expires_in }` (contrato auth). */
export const extractRefreshTokenId = (data: unknown): string | null => {
  const rec = asRecord(data);
  if (!rec) return null;
  const rt = rec.refresh_token;
  if (typeof rt === 'string' && rt) return rt;
  const nested = asRecord(rt);
  if (nested && typeof nested.id === 'string' && nested.id) return nested.id;
  return null;
};

export const extractRefreshExpiresIn = (data: unknown): number | null => {
  const rec = asRecord(data);
  if (!rec) return null;
  const nested = asRecord(rec.refresh_token);
  const n = nested?.expires_in;
  if (typeof n === 'number' && Number.isFinite(n) && n > 0) return n;
  return null;
};

/**
 * Extrai access_token, ACL e id.
 * Retorna null se não houver `access_token`.
 */
export const normalizeLoginPayload = (
  data: LoginPayload | null | undefined,
): NormalizedLogin | null => {
  if (!data || typeof data !== 'object') {
    return null;
  }

  const accessToken =
    typeof data.access_token === 'string' && data.access_token
      ? data.access_token
      : '';

  if (!accessToken) {
    return null;
  }

  const nestedUser = asRecord(data.user);
  const nestedAcl = asRecord(nestedUser?.acl);
  const rootAcl = asRecord(data.acl);

  const acl = nestedAcl ?? rootAcl;

  const userId =
    (typeof nestedUser?.id === 'string' && nestedUser.id) ||
    (typeof data._id === 'string' && data._id) ||
    null;

  const email =
    (typeof nestedUser?.email === 'string' && nestedUser.email) ||
    (typeof data.email === 'string' && data.email) ||
    null;

  const scopes = asScopes(data.scopes) ?? asScopes(nestedUser?.scopes) ?? null;

  return {
    accessToken,
    acl,
    userId,
    email,
    scopes,
    refreshToken: extractRefreshTokenId(data),
    refreshExpiresIn: extractRefreshExpiresIn(data),
    raw: data,
  };
};

/**
 * Meta persistido em `current_user_meta`.
 * O envelope JWT deixa `email` em `user.email`; o panel-logs lê no raiz.
 */
export const buildStoredUserMeta = (
  normalized: NormalizedLogin,
): Record<string, unknown> => ({
  ...normalized.raw,
  ...(normalized.userId
    ? { _id: normalized.userId, id: normalized.userId }
    : {}),
  ...(normalized.email ? { email: normalized.email } : {}),
  ...(normalized.scopes ? { scopes: normalized.scopes } : {}),
});

type AuditActor = {
  user_email?: string;
  user_id?: string;
};

const asEmail = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

const asUserId = (value: unknown): string | undefined =>
  typeof value === 'string' && value ? value : undefined;

/** Ator de auditoria a partir do meta (raiz JWT ou `user.*` aninhado). */
export const resolveAuditActor = (
  meta: unknown,
  fallbackEmail?: string | null,
): AuditActor => {
  const rec = asRecord(meta);
  const nested = asRecord(rec?.user);
  const user_email =
    asEmail(rec?.email) || asEmail(nested?.email) || asEmail(fallbackEmail);
  const user_id =
    asUserId(rec?._id) ||
    asUserId(rec?.id) ||
    asUserId(nested?.id) ||
    asUserId(nested?._id);

  return {
    ...(user_email ? { user_email } : {}),
    ...(user_id ? { user_id } : {}),
  };
};
